import { Component, inject, OnInit, Renderer2 } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { SideNavComponent } from "./core/component/sidenav/side-nav.component";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { SCRIPTS, ScriptsService } from "./core/service/scripts.service";
import { EnvironmentService } from "../environments/environment.service";
import { Title } from "@angular/platform-browser";
import { SpinnerService } from "./core/service/spinner.service";
import { AuthService } from "./domain/auth/service/auth.service";
import { ShowUIService } from "./core/service/show-ui.service";
import { NotificationComponent } from "./domain/shared/component/notification/notification.component";
import { SpinnerComponent } from "./domain/shared/component/spinner/spinner.component";
import { HorizontalNavBarComponent } from "./core/component/hornavbar/horizontal-nav-bar.component";
import { AuthGuard } from "./util/guards/login.guard";
import { IpApiService } from "./core/service/ip-api.service";
import { NgClass } from "@angular/common";
import { ModalStayComponent } from "./core/component/modal-stay/modal-stay.component";
import {
  UnsavedChangesModalWrapperComponent,
} from "./domain/shared/component/unsaved-changes-modal-wrapper/unsaved-changes-modal-wrapper.component";
import { InitStateBusinessService } from "./core/service/init-state-business.service";

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    SideNavComponent,
    NotificationComponent,
    SpinnerComponent,
    HorizontalNavBarComponent,
    NgClass,
    ModalStayComponent,
    UnsavedChangesModalWrapperComponent,
  ],
  providers: [AuthGuard, IpApiService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
})
export class AppComponent implements OnInit {
  private _destroyStream: Subject<void> = new Subject<void>();
  private title = inject(Title);
  public router = inject(Router);
  private scriptsService = inject(ScriptsService);
  private renderer = inject(Renderer2);
  private env = inject(EnvironmentService);
  private showUIService = inject(ShowUIService);
  public spinner = inject(SpinnerService);
  private authService = inject(AuthService);
  private initService = inject(InitStateBusinessService);
  private isMetricsActivated = this.env.isProduction();

  show = {
    sideNav: true,
    horNav: true,
  };

  isLoggedIn = false;
  isSpinnerActive: boolean;

  constructor() {
    if (this.env.isStaging()) {
      this.title.setTitle(this.getAppTitleForTestEnvironments());
    }
  }

  ngOnInit(): void {
    const { isMetricsActivated } = this;
    if (isMetricsActivated) {
      // this.scriptsService.insertJsScriptText(this.renderer, SCRIPTS.ym); // please do not remove this comment
      this.scriptsService.loadJsScript(this.renderer, SCRIPTS.google.url);

      this.scriptsService.insertJsScriptText(this.renderer, SCRIPTS.stylesCssLinkPatching);
      this.scriptsService.insertJsScriptText(this.renderer, SCRIPTS.google.init);
    }

    this.authService.isLoggedInState$
      .subscribe((v) => {
        if (v && this.isLoggedIn !== v) {
          this.initService.refreshInitData();
        }
        this.isLoggedIn = v;
      });

    this.showUIService.showHorNavBar
      .pipe(
        takeUntil(this._destroyStream),
        filter((v) => v !== null)
      )
      .subscribe(
        (v) => {
          this.show.horNav = v;
        });

    this.showUIService.showSideNavBar
      .pipe(
        takeUntil(this._destroyStream),
        filter((v) => v !== null)
      )
      .subscribe(
        (v) => {
          this.show.sideNav = v;
        });

    this.spinner.isActive.pipe(takeUntil(this._destroyStream)).subscribe((isActive) => {
      if (isActive === undefined) {
        return;
      }
      this.isSpinnerActive = isActive;
    });
  }

  private getAppTitleForTestEnvironments(): string {
    const hostname = window.location.hostname;
    if (hostname.includes('dev')) {
      return AppTitleForTestEnvironments.DEV;
    }
    if (hostname.includes('pre-prod')) {
      return AppTitleForTestEnvironments.PRE_PROD;
    }
    if (hostname.includes('qa-unit')) {
      return AppTitleForTestEnvironments.QA_UNIT;
    }
    if (hostname.includes('qa')) {
      return AppTitleForTestEnvironments.QA;
    }
  }
}

enum AppTitleForTestEnvironments {
  QA = 'Models QA',
  PRE_PROD = 'Models Pre-prod',
  DEV = 'Models Dev',
  QA_UNIT = 'Models QA Unit',
}
